import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../stores";
import { useToast } from "../components/ui/toast";
import { livestreamAPI } from "../services/api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
	Card,
	CardContent,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "../components/ui/dialog";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../components/ui/table";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
	Video,
	Plus,
	Users,
	ChevronLeft,
	ChevronRight,
	Trash2,
	Radio,
	FileVideo,
	Share2,
	MoreHorizontal,
	Loader2,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { 
	DropdownMenu, 
	DropdownMenuContent, 
	DropdownMenuItem, 
	DropdownMenuTrigger,
	DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import PublicStreamLinksDialog from '../components/PublicStreamLinksDialog';

interface LiveStream {
	id: string;
	title: string;
	description?: string;
	type: "video" | "audio" | "video_audio";
	status: "created" | "live" | "ended";
	current_viewers: number;
	is_recording_enabled: boolean;
	is_chat_enabled: boolean;
	created_at: string;
	user: {
		id: string;
		first_name: string;
		last_name: string;
	};
}

const LiveStreams: React.FC = () => {
	const [livestreams, setLivestreams] = useState<LiveStream[]>([]);
	const [loading, setLoading] = useState(true);
	const [showCreateDialog, setShowCreateDialog] = useState(false);
	// Removed showStreamUrlsDialog state since it's no longer needed
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);
	const [streamToDelete, setStreamToDelete] = useState<LiveStream | null>(null);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [streamTitle, setStreamTitle] = useState("");
	const [streamDescription, setStreamDescription] = useState("");
	const [streamType, setStreamType] = useState<
		"video" | "audio" | "video_audio"
	>("video_audio");
	const [isRecordingEnabled, setIsRecordingEnabled] = useState(false);
	const [isChatEnabled, setIsChatEnabled] = useState(true);
	const [createLoading, setCreateLoading] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage] = useState(10);
	const [statusFilter, setStatusFilter] = useState<string>("");
	
	// Public stream links dialog state
	const [showPublicLinksDialog, setShowPublicLinksDialog] = useState(false);
	const [selectedStreamForPublicLinks, setSelectedStreamForPublicLinks] = useState<LiveStream | null>(null);

	const user = useAuthStore((state) => state.user);
	const { addToast } = useToast();
	const navigate = useNavigate();

	useEffect(() => {
		fetchLivestreams();
	}, [statusFilter]);

	const fetchLivestreams = async () => {
		try {
			const params = statusFilter ? { status: statusFilter } : undefined;
			const response = await livestreamAPI.list(params);
			setLivestreams(response.data.data.livestreams || []);
		} catch (error) {
			console.error("Failed to fetch livestreams:", error);
			addToast({
				title: "Error",
				description: "Failed to load live streams. Please refresh the page.",
				variant: "destructive",
			});
			setLivestreams([]);
		} finally {
			setLoading(false);
		}
	};
	

	const handleCreateStream = async (e: React.FormEvent) => {
		e.preventDefault();
		setCreateLoading(true);

		try {
			const response = await livestreamAPI.create({
				title: streamTitle || "New Live Stream",
				description: streamDescription,
				type: streamType,
				is_recording_enabled: isRecordingEnabled,
				is_chat_enabled: isChatEnabled,
			});

			console.log("Create stream response:", response);

			const newStream = response.data.data.livestream;
			if (newStream && newStream.id) {
				setLivestreams([newStream, ...livestreams]);
				// Show the public links dialog directly instead of the StreamUrlsDialog
				setSelectedStreamForPublicLinks(newStream);
				setShowPublicLinksDialog(true);
			} else {
				console.error("Invalid stream data:", newStream);
				throw new Error("Invalid stream data received from server");
			}
			setStreamTitle("");
			setStreamDescription("");
			setStreamType("video_audio");
			setIsRecordingEnabled(false);
			setIsChatEnabled(true);
			setShowCreateDialog(false);
			// Don't show the StreamUrlsDialog anymore

			addToast({
				title: "Stream Created",
				description: `Your live stream has been created successfully! You can now start publishing or generate public URLs to share.`,
				variant: "success",
			});
		} catch (error) {
			console.error("Failed to create livestream:", error);
			addToast({
				title: "Creation Failed",
				description: "Failed to create live stream. Please try again.",
				variant: "destructive",
			});
		} finally {
			setCreateLoading(false);
		}
	};

	const handleDeleteStream = async () => {
		if (!streamToDelete) return;

		setDeleteLoading(true);
		try {
			await livestreamAPI.delete(streamToDelete.id);
			setLivestreams(livestreams.filter((s) => s.id !== streamToDelete.id));
			setShowDeleteDialog(false);
			setStreamToDelete(null);

			addToast({
				title: "Stream Deleted",
				description: "Live stream has been deleted successfully.",
				variant: "success",
			});
		} catch (error) {
			console.error("Failed to delete livestream:", error);
			addToast({
				title: "Delete Failed",
				description: "Failed to delete live stream. Please try again.",
				variant: "destructive",
			});
		} finally {
			setDeleteLoading(false);
		}
	};

	const openDeleteDialog = (livestream: LiveStream) => {
		setStreamToDelete(livestream);
		setShowDeleteDialog(true);
	};

	const copyToClipboard = (text: string, label: string) => {
		navigator.clipboard.writeText(text);
		addToast({
			title: "Link Copied",
			description: `${label} has been copied to clipboard.`,
			variant: "success",
		});
	};

	const navigateToPublisher = (roomName: string) => {
		navigate(`/dashboard/stream/${roomName}/publish`);
	};

	const navigateToViewer = (roomName: string) => {
		navigate(`/dashboard/stream/${roomName}/view`);
	};

	// Pagination logic
	const totalPages = Math.ceil(livestreams.length / itemsPerPage);
	const paginatedStreams = livestreams.slice(
		(currentPage - 1) * itemsPerPage,
		currentPage * itemsPerPage,
	);

	const formatDate = (dateString: string) => {
		return new Intl.DateTimeFormat("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		}).format(new Date(dateString));
	};

	const getStreamTypeIcon = (type: string) => {
		switch (type) {
			case "video":
				return <Video className="h-4 w-4" />;
			case "audio":
				return <Radio className="h-4 w-4" />;
			default:
				return <Video className="h-4 w-4" />;
		}
	};

	const getStatusBadgeVariant = (status: string) => {
		switch (status) {
			case "created":
				return "secondary";
			case "live":
				return "default";
			case "ended":
				return "outline";
			default:
				return "secondary";
		}
	};

	const openPublicLinksDialog = (stream: LiveStream) => {
		setSelectedStreamForPublicLinks(stream);
		setShowPublicLinksDialog(true);
	};

	return (
		<Layout
			title="Live Streams"
			description="Manage your live streams and discover new content"
		>
			<TooltipProvider>
				<div className="mobile-padding py-4 sm:py-6 lg:py-8 space-y-4 sm:space-y-6">
				{/* Header with Filters and Create Action */}
				<div className="flex justify-between items-center gap-4">
					{/* Status Filter */}
					<select
						value={statusFilter}
						onChange={(e) => setStatusFilter(e.target.value)}
						className="flex h-10 min-w-40 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
					>
						<option value="">All Statuses</option>
						<option value="created">Created</option>
						<option value="live">Live</option>
						<option value="ended">Ended</option>
					</select>

					{/* Create Stream Button */}
					<Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
						<DialogTrigger
							className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
							onClick={() => setShowCreateDialog(true)}
						>
							<Plus className="h-4 w-4" />
							Create Stream
						</DialogTrigger>
						<DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-hidden p-0">
							<form onSubmit={handleCreateStream} className="flex flex-col max-h-[85vh]">
								<div className="px-6 pt-6 pb-4 flex-shrink-0">
									<DialogHeader className="space-y-3">
										<DialogTitle>Create New Live Stream</DialogTitle>
										<DialogDescription>
											Set up a new live stream with your preferences
										</DialogDescription>
									</DialogHeader>
								</div>
								
								<div className="flex-1 overflow-y-auto px-6">
									<div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-4">
										{/* Left Column - Basic Info */}
										<div className="space-y-4">
											<div className="space-y-3">
												<Label htmlFor="title" className="text-sm font-medium">Title</Label>
												<Input
													id="title"
													placeholder="Stream title"
													value={streamTitle}
													onChange={(e) => setStreamTitle(e.target.value)}
													required
													className="w-full"
												/>
											</div>
											<div className="space-y-3">
												<Label htmlFor="description" className="text-sm font-medium">Description (optional)</Label>
												<Textarea
													id="description"
													placeholder="Stream description"
													value={streamDescription}
													onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
														setStreamDescription(e.target.value)
													}
													rows={5}
													className="w-full resize-none"
												/>
											</div>
										</div>
										
										{/* Right Column - Settings */}
										<div className="space-y-5 md:border-l md:pl-6">
											<div className="text-sm font-semibold">Stream Settings</div>
											
											<div className="space-y-3">
												<Label htmlFor="type" className="text-sm font-medium">Stream Type</Label>
												<select
													id="type"
													value={streamType}
													onChange={(e) =>
														setStreamType(
															e.target.value as "video" | "audio" | "video_audio",
														)
													}
													className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
												>
													<option value="video_audio">Video + Audio</option>
													<option value="video">Video Only</option>
													<option value="audio">Audio Only</option>
												</select>
											</div>
											
											<div className="flex items-start justify-between gap-4 py-2">
												<div className="space-y-1 flex-1">
													<Label htmlFor="recording" className="text-sm font-medium cursor-pointer">
														Enable Recording
													</Label>
													<div className="text-sm text-muted-foreground leading-relaxed">
														Automatically record this livestream
													</div>
												</div>
												<Switch
													id="recording"
													checked={isRecordingEnabled}
													onCheckedChange={setIsRecordingEnabled}
													className="mt-1 flex-shrink-0"
												/>
											</div>
											
											<div className="flex items-center space-x-2 hidden">
												<input
													type="checkbox"
													id="chat"
													checked={isChatEnabled}
													onChange={(e) => setIsChatEnabled(e.target.checked)}
													className="w-4 h-4"
												/>
												<Label htmlFor="chat">Enable Chat</Label>
											</div>
										</div>
									</div>
								</div>
								
								<div className="px-6 pb-6 pt-4 border-t flex-shrink-0">
									<DialogFooter className="gap-2">
										<Button
											type="button"
											variant="outline"
											onClick={() => setShowCreateDialog(false)}
										>
											Cancel
										</Button>
										<Button type="submit" disabled={createLoading}>
											{createLoading ? "Creating..." : "Create Stream"}
										</Button>
									</DialogFooter>
								</div>
							</form>
						</DialogContent>
					</Dialog>
				</div>

				{/* Delete Stream Confirmation Dialog */}
				<Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
					<DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-hidden p-0">
						<div className="flex flex-col max-h-[85vh]">
							<div className="px-6 pt-6 pb-6 flex-shrink-0">
								<DialogHeader className="space-y-4">
									<DialogTitle>Delete Live Stream</DialogTitle>
									<DialogDescription>
										Are you sure you want to delete this live stream? This action
										cannot be undone. All viewers will be disconnected and the
										stream URLs will no longer work.
									</DialogDescription>
								</DialogHeader>
							</div>
							
							<div className="flex-1 overflow-y-auto px-6 pb-6">
								{streamToDelete && (
									<div className="bg-muted p-5 rounded-lg space-y-4">
										<h4 className="font-medium text-base">{streamToDelete.title}</h4>
										{streamToDelete.description && (
											<p className="text-sm text-muted-foreground">
												{streamToDelete.description}
											</p>
										)}
										<div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 text-sm text-muted-foreground">
											<span className="flex items-center gap-1">
												<Users className="h-4 w-4" />
												{streamToDelete.current_viewers} viewers
											</span>
											<Badge variant={getStatusBadgeVariant(streamToDelete.status)}>
												{streamToDelete.status}
											</Badge>
										</div>
									</div>
								)}
							</div>
							
							<div className="px-6 pb-6 pt-5 border-t flex-shrink-0">
								<DialogFooter className="flex-col-reverse sm:flex-row gap-2">
									<Button
										variant="outline"
										onClick={() => setShowDeleteDialog(false)}
										disabled={deleteLoading}
										className="w-full sm:w-auto"
									>
										Cancel
									</Button>
									<Button
										variant="destructive"
										onClick={handleDeleteStream}
										disabled={deleteLoading}
										className="w-full sm:w-auto"
									>
										{deleteLoading ? "Deleting..." : "Delete Stream"}
									</Button>
								</DialogFooter>
							</div>
						</div>
					</DialogContent>
				</Dialog>

				{/* Streams Table */}
				<Card>
					<CardContent className="p-0">
						{loading ? (
							<div className="flex flex-col items-center justify-center py-16">
								<Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
								<p className="text-sm text-muted-foreground">Loading live streams...</p>
							</div>
						) : livestreams.length === 0 ? (
							<div className="text-center py-12">
								<div className="h-16 w-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
									<Video className="h-8 w-8 text-muted-foreground" />
								</div>
								<h3 className="text-lg font-medium mb-2">
									No live streams found
								</h3>
								<p className="text-muted-foreground mb-4">
									Create your first live stream to get started
								</p>
								<Button onClick={() => setShowCreateDialog(true)}>
									<Plus className="h-4 w-4 mr-2" />
									Create Stream
								</Button>
							</div>
						) : (
							<>
								{/* Desktop Table View */}
								<div className="hidden md:block rounded-md">
									<Table>
										<TableHeader>
											<TableRow>
												<TableHead>Title</TableHead>
												<TableHead>Type</TableHead>
												<TableHead>Status</TableHead>
												<TableHead>Viewers</TableHead>
												<TableHead>Created</TableHead>
												<TableHead className="text-right">Actions</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{paginatedStreams
												.filter((stream) => stream && stream.id)
												.map((stream) => (
													<TableRow key={stream.id}>
														<TableCell className="font-medium">
															<div>
																<div>{stream.title}</div>
																{stream.description && (
																	<div className="text-sm text-muted-foreground truncate max-w-[200px]">
																		{stream.description}
																	</div>
																)}
															</div>
														</TableCell>
														<TableCell>
															<div className="flex items-center gap-2">
																{getStreamTypeIcon(stream.type)}
																<span className="capitalize">
																	{stream.type.replace("_", " + ")}
																</span>
															</div>
														</TableCell>
														<TableCell>
															<Badge
																variant={getStatusBadgeVariant(stream.status)}
															>
																{stream.status}
															</Badge>
														</TableCell>
														<TableCell>
															<div className="flex items-center gap-1">
																<Tooltip>
																	<TooltipTrigger asChild>
																		<Users className="h-4 w-4" />
																	</TooltipTrigger>
																	<TooltipContent>
																		Current Viewers
																	</TooltipContent>
																</Tooltip>
																{stream.current_viewers}
															</div>
														</TableCell>
														<TableCell className="text-muted-foreground">
															{formatDate(stream.created_at)}
														</TableCell>
														<TableCell className="text-right">
															<div className="flex items-center justify-end gap-2">
																<Tooltip>
																	<TooltipTrigger asChild>
																		<Button
																			size="sm"
																			variant="outline"
																			onClick={() => openPublicLinksDialog(stream)}
																		>
																			<Share2 className="h-4 w-4 mr-2" />
																			Links
																		</Button>
																	</TooltipTrigger>
																	<TooltipContent>
																		Public Publisher and Viewer Links
																	</TooltipContent>
																</Tooltip>
																<Tooltip>
																	<TooltipTrigger asChild>
																		<Button
																			size="sm"
																			variant="outline"
																			onClick={() => navigate(`/dashboard/livestream/${stream.id}/recordings`)}
																		>
																			<FileVideo className="h-4 w-4 mr-2" />
																			Recordings
																		</Button>
																	</TooltipTrigger>
																	<TooltipContent>
																		View Recordings
																	</TooltipContent>
																</Tooltip>
																<Tooltip>
																	<TooltipTrigger asChild>
																		<Button
																			size="sm"
																			variant="outline"
																			onClick={() => openDeleteDialog(stream)}
																			className="text-red-600 hover:text-red-700 hover:bg-red-50"
																		>
																			<Trash2 className="h-4 w-4" />
																		</Button>
																	</TooltipTrigger>
																	<TooltipContent>
																		Delete Stream
																	</TooltipContent>
																</Tooltip>
															</div>
														</TableCell>
													</TableRow>
												))}
										</TableBody>
									</Table>
								</div>

								{/* Mobile Card View */}
								<div className="md:hidden divide-y divide-border">
									{paginatedStreams
										.filter((stream) => stream && stream.id)
										.map((stream) => (
											<div key={stream.id} className="p-4 space-y-3">
												{/* Header with Title and Actions */}
												<div className="flex items-start justify-between gap-3">
													<div className="flex-1 min-w-0">
														<h3 className="font-semibold text-base truncate">{stream.title}</h3>
														{stream.description && (
															<p className="text-sm text-muted-foreground mt-1 line-clamp-2">
																{stream.description}
															</p>
														)}
													</div>
													<DropdownMenu>
														<DropdownMenuTrigger asChild>
															<Button
																size="sm"
																variant="ghost"
																className="h-9 w-9 p-0 touch-target flex-shrink-0"
															>
																<MoreHorizontal className="h-5 w-5" />
															</Button>
														</DropdownMenuTrigger>
														<DropdownMenuContent align="end">
															<DropdownMenuItem
																onClick={() => openPublicLinksDialog(stream)}
															>
																<Share2 className="h-4 w-4 mr-2" />
																Public Links
															</DropdownMenuItem>
															<DropdownMenuItem
																onClick={() => navigate(`/dashboard/livestream/${stream.id}/recordings`)}
															>
																<FileVideo className="h-4 w-4 mr-2" />
																View Recordings
															</DropdownMenuItem>
															<DropdownMenuSeparator />
															<DropdownMenuItem
																onClick={() => openDeleteDialog(stream)}
																className="text-red-600 hover:text-red-700"
															>
																<Trash2 className="h-4 w-4 mr-2" />
																Delete Stream
															</DropdownMenuItem>
														</DropdownMenuContent>
													</DropdownMenu>
												</div>

												{/* Type, Status and Stats */}
												<div className="flex items-center gap-3 flex-wrap">
													<div className="flex items-center gap-1.5 text-sm">
														{getStreamTypeIcon(stream.type)}
														<span className="capitalize text-muted-foreground">
															{stream.type.replace("_", " + ")}
														</span>
													</div>
													<Badge
														variant={getStatusBadgeVariant(stream.status)}
													>
														{stream.status}
													</Badge>
													<div className="flex items-center gap-1 text-sm text-muted-foreground">
														<Users className="h-4 w-4" />
														<span>{stream.current_viewers} viewers</span>
													</div>
												</div>

												{/* Created Date */}
												<div className="text-xs text-muted-foreground">
													Created {formatDate(stream.created_at)}
												</div>
											</div>
										))}
								</div>
								
								{/* Pagination */}
								{totalPages > 1 && (
									<div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-4 border-t">
										<div className="text-sm text-muted-foreground">
											Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
											{Math.min(currentPage * itemsPerPage, livestreams.length)}{" "}
											of {livestreams.length} entries
										</div>
										<div className="flex items-center space-x-2">
											<Button
												variant="outline"
												size="sm"
												onClick={() =>
													setCurrentPage(Math.max(1, currentPage - 1))
												}
												disabled={currentPage === 1}
												className="touch-target"
											>
												<ChevronLeft className="h-4 w-4" />
												<span className="hidden sm:inline ml-1">Previous</span>
											</Button>
											<div className="flex items-center space-x-1">
												{Array.from(
													{ length: totalPages },
													(_, i) => i + 1,
												).map((page) => (
													<Button
														key={page}
														variant={
															currentPage === page ? "default" : "outline"
														}
														size="sm"
														onClick={() => setCurrentPage(page)}
														className="w-9 h-9 p-0 touch-target"
													>
														{page}
													</Button>
												))}
											</div>
											<Button
												variant="outline"
												size="sm"
												onClick={() =>
													setCurrentPage(Math.min(totalPages, currentPage + 1))
												}
												disabled={currentPage === totalPages}
												className="touch-target"
											>
												<span className="hidden sm:inline mr-1">Next</span>
												<ChevronRight className="h-4 w-4" />
											</Button>
										</div>
									</div>
								)}
							</>
						)}
					</CardContent>
				</Card>

				{/* Public Stream Links Dialog */}
				<PublicStreamLinksDialog
					stream={selectedStreamForPublicLinks}
					open={showPublicLinksDialog}
					onOpenChange={setShowPublicLinksDialog}
				/>
				</div>
			</TooltipProvider>
		</Layout>
	);
};

export default LiveStreams;